import React, { useRef } from "react";
import { Link } from "react-router-dom";
import Markdown from "react-markdown";
import foundryMarkdown from "../../../lib/FoundryMarkdown";
import { checkEnv } from "../../helper/helper";
import {
  extractFoundryObjectReferences,
  buildSourceNumberMap,
} from "../../helper/helper";
import { useEventSources } from "../../hooks/useEventSources";
import InternalLinkSourcePill from "../InternalLinkSourcePill/InternalLinkSourcePill";
import { Osdk } from "@osdk/client";
import { Analysis } from "@unruly-osdk/sdk";
import {
  SharedToLinkedInToast,
  SharedToLinkedInToastHandle,
} from "../SharedToLinkedInToast/SharedToLinkedInToast";
import { ShareDialog, ShareDialogHandle } from "../ShareDialog/ShareDialog";

const shortlinkUrl = import.meta.env.VITE_FOUNDRY_APP_SHORTLINK_URL;
checkEnv(shortlinkUrl, "VITE_FOUNDRY_APP_SHORTLINK_URL");

interface ReportContentProps {
  reportText: string;
  userId?: string;
  onLinkClick?: React.MouseEventHandler<HTMLAnchorElement>;
  openLinksInNewTab?: boolean;
  onOpenAnalysis?: (analysis: Osdk.Instance<Analysis>, eventId: string) => void;
}

const ReportContent: React.FC<ReportContentProps> = ({
  reportText,
  userId,
  onLinkClick,
  openLinksInNewTab = false,
  onOpenAnalysis,
}) => {
  const referencedEvents = extractFoundryObjectReferences(reportText);
  const sourceNumberMap = buildSourceNumberMap(referencedEvents);

  const { eventSources, isLoading, missingEventIds } =
    useEventSources(referencedEvents);

  const toastRef = useRef<SharedToLinkedInToastHandle>(null);

  const dialog = useRef<ShareDialogHandle>(null);

  return (
    <>
      <Markdown
        remarkPlugins={[foundryMarkdown]}
        components={{
          a({ children, href }) {
            const isInternalLink = href && href.startsWith("/");
            if (isInternalLink) {
              return (
                <InternalLinkSourcePill
                  href={href}
                  eventSources={eventSources}
                  isLoading={isLoading}
                  missingEventIds={missingEventIds}
                  userId={userId}
                  sourceNumberMap={sourceNumberMap}
                  onOpenAnalysis={onOpenAnalysis}
                  showShareDialog={(eventId) => dialog.current?.show(eventId)}
                />
              );
            }

            return (
              <Link
                to={href || "#"}
                {...(openLinksInNewTab && {
                  target: "_blank",
                  rel: "noopener noreferrer",
                })}
                onClick={onLinkClick}
              >
                {children}
              </Link>
            );
          },
        }}
      >
        {reportText}
      </Markdown>
      <ShareDialog
        onShareSuccess={(postId) => toastRef.current?.shareSuccess(postId)}
        ref={dialog}
      />
      <SharedToLinkedInToast ref={toastRef} />
    </>
  );
};

export default ReportContent;
